/**
 * Enthalpy change from average bond energies:
 *
 *     ΔH ≈ Σ D(bonds broken) − Σ D(bonds formed)
 *
 * Bonds are typed as text ("4 C–H, 2 O=O") so a reaction can be written down quickly and
 * copied. A token is an optional count, a bond made of two element symbols and a bond
 * symbol (– or - for single, = double, ≡ or # triple), and an optional `@value` that
 * replaces the table's energy for that token, e.g. `2 C=O@799`. Counts and energies use a
 * decimal point, because the comma separates bonds.
 *
 * Bond energies are for gaseous molecules, so the result is the gas-phase ΔH and it is
 * a rough estimate (OpenStax: "a rough estimate, not an exact value").
 */

import { type Bond, findBond } from "@/lib/tool-kit/chem-reference/bond-data";

export interface BondEntry {
  bond: Bond;
  count: number;
  /** The energy used, kJ/mol: the table's, or the `@` override. */
  energy: number;
  overridden: boolean;
}

export interface ParseProblem {
  token: string;
  reason: "syntax" | "unknown" | "count" | "energy";
}

export interface ParsedBonds {
  entries: BondEntry[];
  problems: ParseProblem[];
}

const SEPARATORS = /[,;+\n]/;
const DASHES = /[‐-―−]/g;
const TRIPLE = /[≡#]/g;
const TOKEN =
  /^(?:(\d+(?:\.\d+)?)\s*[×*]?\s*)?([A-Z][a-z]?)\s*([-=≡])\s*([A-Z][a-z]?)\s*(?:@\s*(\d+(?:\.\d+)?))?$/;
const ORDER_OF: Record<string, 1 | 2 | 3> = { "-": 1, "=": 2, "≡": 3 };
const MAX_COUNT = 1000;

const toNumber = (text: string) => Number(text);

function parseToken(token: string): BondEntry | ParseProblem {
  const normalised = token.replace(DASHES, "-").replace(TRIPLE, "≡").trim();
  const match = TOKEN.exec(normalised);
  if (!match) {
    return { token, reason: "syntax" };
  }
  const [, countText, a, symbol, b, energyText] = match;
  const count = countText === undefined ? 1 : toNumber(countText);
  if (!(count > 0 && count <= MAX_COUNT)) {
    return { token, reason: "count" };
  }
  const bond = findBond(a, b, ORDER_OF[symbol]);
  if (!bond) {
    return { token, reason: "unknown" };
  }
  if (energyText === undefined) {
    return { bond, count, energy: bond.energy, overridden: false };
  }
  const energy = toNumber(energyText);
  return energy > 0
    ? { bond, count, energy, overridden: true }
    : { token, reason: "energy" };
}

/** Read a list of bonds. Problems are reported per token; the good tokens still count. */
export function parseBondList(text: string): ParsedBonds {
  const entries: BondEntry[] = [];
  const problems: ParseProblem[] = [];
  for (const raw of text.split(SEPARATORS)) {
    if (raw.trim() === "") {
      continue;
    }
    const result = parseToken(raw);
    if ("bond" in result) {
      entries.push(result);
    } else {
      problems.push(result);
    }
  }
  return { entries, problems };
}

/** Write entries back in the form the parser reads: "4 C–H, 2 O=O@799". */
export function formatBondList(entries: readonly BondEntry[]): string {
  return entries
    .map((entry) => {
      const count = entry.count === 1 ? "" : `${entry.count} `;
      const energy = entry.overridden ? `@${entry.energy}` : "";
      return `${count}${entry.bond.label}${energy}`;
    })
    .join(", ");
}

/** Add one of a bond to the text, bumping the count if it is already there. */
export function addBond(text: string, bond: Bond): string {
  const { entries } = parseBondList(text);
  const existing = entries.find(
    (entry) => entry.bond === bond && !entry.overridden
  );
  if (existing) {
    existing.count += 1;
  } else {
    entries.push({ bond, count: 1, energy: bond.energy, overridden: false });
  }
  return formatBondList(entries);
}

export const sumEnergy = (entries: readonly BondEntry[]): number =>
  entries.reduce((total, entry) => total + entry.count * entry.energy, 0);

export interface BondEnthalpy {
  broken: number;
  formed: number;
  /** kJ per mole of reaction as written. */
  delta: number;
}

export function bondEnthalpy(
  broken: readonly BondEntry[],
  formed: readonly BondEntry[]
): BondEnthalpy {
  const b = sumEnergy(broken);
  const f = sumEnergy(formed);
  return { broken: b, formed: f, delta: b - f };
}

export const KJ_PER_KCAL = 4.184;

export type Heat = "exothermic" | "endothermic" | "none";

export function heatKind(delta: number): Heat {
  if (delta < 0) {
    return "exothermic";
  }
  return delta > 0 ? "endothermic" : "none";
}

export interface BondPreset {
  id: string;
  /** The reaction as a formula equation (not translated). */
  label: string;
  broken: string;
  formed: string;
}

export const BOND_PRESETS: readonly BondPreset[] = [
  {
    id: "methane",
    label: "CH₄ + 2 O₂ → CO₂ + 2 H₂O(g)",
    broken: "4 C–H, 2 O=O",
    formed: "2 C=O, 4 O–H",
  },
  {
    id: "hcl",
    label: "H₂ + Cl₂ → 2 HCl",
    broken: "H–H, Cl–Cl",
    formed: "2 H–Cl",
  },
  {
    id: "hf",
    label: "H₂ + F₂ → 2 HF",
    broken: "H–H, F–F",
    formed: "2 H–F",
  },
  {
    id: "ammonia",
    label: "N₂ + 3 H₂ → 2 NH₃",
    broken: "N≡N, 3 H–H",
    formed: "6 N–H",
  },
  {
    id: "ethene",
    label: "C₂H₄ + H₂ → C₂H₆",
    broken: "C=C, H–H",
    formed: "C–C, 2 C–H",
  },
];
